import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import useHandleErrors from '../../utils/user.utils';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import { utils, write } from 'xlsx';
import moment from 'moment';


const PoliciesComponent = () => {
    const [policies, setPolicies] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [filteredData, setFilteredData] = useState([]);
    const handleErrors = useHandleErrors();
    const token = localStorage.getItem("token");
    const hasFetchedRef = useRef(false);

    useEffect(() => {
        const fetchPolicies = async () => {
            try {
                const path = "/policyDetails";
                const URL = process.env.REACT_APP_BE_URL + path;
                const response = await axios.get(URL, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    }
                });
                if (response.status === 200) {
                    setPolicies(response.data.policies);
                }
                else {
                    handleErrors(response);
                }
            } catch (error) {
                if (error.response) {
                    if (error.response.status === 404) {
                        setPolicies([]);
                    } else {
                        handleErrors(error.response);
                    }
                } else {
                    toast.error("Something went wrong. Please try again later.", {
                        position: toast.POSITION.TOP_CENTER,
                    });
                }
                console.error('Error fetching policies:', error);
            }
        };
        if (!hasFetchedRef.current) {
            fetchPolicies();
            hasFetchedRef.current = true;
        }
    }, []);


    const sendEmail = (email) => {
        // Excel generation logic
        const ws = utils.json_to_sheet(filteredData);
        const wb = utils.book_new();
        utils.book_append_sheet(wb, ws, 'Policies');
        const excelBuffer = write(wb, { bookType: 'xlsx', type: 'array' });

        // Convert the Excel file to a Base64 string
        const data = new Blob([excelBuffer], { type: 'application/octet-stream' });
        const reader = new FileReader();
        reader.readAsDataURL(data);
        reader.onloadend = () => {
            const base64data = reader.result.split(',')[1]; // Get the Base64 string without the prefix

            // Create a downloadable link for the attachment
            const link = document.createElement('a');
            link.href = reader.result;
            link.download = 'PoliciesReport.xlsx';

            // Simulate a click to download the attachment
            link.click();

            // Prepare the mailto link (without attachment)
            const subject = "Policies List";
            const body = 'Please find the attached file.';
            const mailtoLink = `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

            toast.info('The Excel file has been generated and downloaded. Please attach the file manually to your email.', {
                position: toast.POSITION.TOP_CENTER,
            });

            setTimeout(() => {
                window.location.href = mailtoLink;
            }, 3000);

        };
    };

    const shareData = {
        title: 'Policies List',
        text: 'Check out the Policies List',
        url: window.location.href
    };

    const handleShare = async () => {
        try {
            await navigator.share(shareData);
        } catch (err) {
            console.error('Error sharing:', err);
        }
    };

    const exportToExcel = () => {
        const worksheet = XLSX.utils.json_to_sheet(filteredData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Guest");
        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
        saveAs(blob, 'PoliciesReport.xlsx');
    };

    useEffect(() => {
        const preloading = document.getElementById("preloading");
        setTimeout(() => {
            if (preloading) {
                preloading.style.display = "none";
            }
        }, 400);
    }, []);

    useEffect(() => {
        setFilteredData(
            policies.filter(policy =>
                policy.policyType.toLowerCase().includes(searchQuery.toLowerCase()) ||
                policy.policyName.toLowerCase().includes(searchQuery.toLowerCase())
            )
        );
    }, [searchQuery, policies]);

    const groupedPolicies = {};
    filteredData.forEach(policy => {
        if (!groupedPolicies[policy.policyType]) {
            groupedPolicies[policy.policyType] = [];
        }
        groupedPolicies[policy.policyType].push(policy);
    });

    return (
        <div>
            <div id="preloading">
                <span className="loader"></span>
            </div>
            <ToastContainer />
            <div class="content-page">
                <div class="container-fluid">
                    <div class="row mb-2">
                        <div class="col">
                            <h3 class="title mb-2">Policies</h3>
                        </div>
                        <div class="col-auto">
                            <Link to={'/Payslips'} data-extra-toggle="tab" data-target="#more-pan" class="btn btn-primary mt-lg-0 mt-4"><i class="ri-fingerprint-line"></i> Payslips</Link>
                        </div>
                    </div>
                    {policies?.length === 0 ? (
                        <div class="container">
                            <div class="row vh-50 justify-content-center align-items-center">
                                <div class="col-auto">
                                    <div class="no-data-wrapper">
                                        <div class="no-data-found">
                                            <i class="ri-file-damage-fill"></i>
                                            <div class="bounce"><i class="ri-search-eye-line"></i></div>
                                            <p>No Data Found</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div>
                            <div className="row align-items-center justify-content-center">
                                <div className="col-lg-5 col-md-8 col-sm-10">
                                    <div className="container-fluid row-rounded mb-0">
                                        <div className="row px-2 py-0">
                                            <div className="input-group mb-0">
                                                <input
                                                    type="search"
                                                    className="form-control search-input"
                                                    placeholder="Keyword search..."
                                                    value={searchQuery}
                                                    onChange={(e) => setSearchQuery(e.target.value)}
                                                />
                                                <div className="input-group-append">
                                                    <button className="btn btn-primary" type="button" title="Search" rel="tooltip">
                                                        <i className="ri-menu-search-line"></i>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <small className="form-text text-muted text-center mt-0"><i className="ri-information-2-fill text-info"></i> Allows: Policy Name - Policy Type - Department</small>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-lg-12">
                                    <div className="card mb-4">
                                        <div className="card-body">
                                            {/* <div className="row">
                                                <div className="col">
                                                    <h5 className="title mt-1 mb-0">View Policies</h5>
                                                </div>
                                                <div className="col-auto">
                                                    <button
                                                        type="button"
                                                        title="Email"
                                                        rel="tooltip"
                                                        className="btn btn-light btn-circle ml-2"
                                                        onClick={() => sendEmail('')}
                                                    >
                                                        <i className="ri-mail-send-fill"></i>
                                                    </button>
                                                    <button
                                                        type="button"
                                                        title="Share"
                                                        rel="tooltip"
                                                        className="btn btn-light btn-circle ml-2"
                                                        onClick={handleShare}
                                                    >
                                                        <i className="ri-share-fill"></i>
                                                    </button>
                                                    <button type="button" title="Export" className="btn btn-light btn-circle ml-2" onClick={exportToExcel}>
                                                        <i className="ri-export-fill"></i>
                                                    </button>
                                                </div>
                                            </div> */}

                                            <div className="row mt-3">
                                                <div className="col-md-12" id="policiesSummaryList">
                                                    {Object.keys(groupedPolicies).map(policyType => (
                                                        <div key={policyType} className="mb-4">
                                                            <h6 className="title accordion-title" data-toggle="collapse" data-target={`#${policyType.replace(' ', '-')}`}><i className="ri-folder-open-line"></i> {policyType}
                                                                <span className="show-policy-details float-right">
                                                                    <div className="row">
                                                                        <div className="col border-right"><i className="ri-file-copy-2-line"></i> Available Policies</div>
                                                                        <div className="col-auto"><span className="text-success"> {groupedPolicies[policyType].length}</span></div>
                                                                    </div>
                                                                </span>
                                                            </h6>
                                                            <div className="collapse show" id={policyType.replace(' ', '-')} data-parent="#policiesSummaryList">
                                                                <div className="row mt-3">
                                                                    {groupedPolicies[policyType].map(policy => (
                                                                        <div key={policy.policyId} className="col-lg-3 col-md-6">
                                                                            <div className="card policy-card mb-4">
                                                                                <div className="card-body">
                                                                                    <div className="row align-items-center justify-content-between mb-2">
                                                                                        <div className="col-auto">
                                                                                            <div className="card ww-app-bg policy-icon">
                                                                                                <div className="card-body p-1">
                                                                                                    <span><i className="ri-file-text-line"></i></span>
                                                                                                </div>
                                                                                            </div>
                                                                                        </div>
                                                                                        <small className="col text-muted text-left px-0">{policy.policyType}</small>
                                                                                        <div className="col-auto pl-0">
                                                                                            <a href={policy.policyFileURL} className="title" title="Download"><i className="ri-download-2-line"></i></a>
                                                                                        </div>
                                                                                    </div>
                                                                                    <a href={policy.policyFileURL}>
                                                                                        <h5 className="title">{policy.policyName}</h5>
                                                                                        <p>{policy.policyDescription}</p>
                                                                                    </a>
                                                                                    <small className="text-muted">Uploaded On: {moment(policy.createdDate).format("DD-MM-YYYY")}</small>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PoliciesComponent;

